import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../hooks/use-dialog';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from './ui/toast';

// Global delete dialog ID
export const DELETE_DIALOG_ID = 'delete-confirm';

interface DeleteDialogData {
  title?: string;
  description?: string;
  itemName?: string;
  confirmText?: string;
  confirmVariant?: 'destructive' | 'default' | 'secondary' | 'outline' | 'ghost' | 'link';
  onConfirm: () => void | Promise<void>;
}

/**
 * A reusable delete confirmation dialog.
 */
export function DeleteConfirmDialog() {
  const { t } = useTranslation();
  const { isOpen, data, close } = useDialog<DeleteDialogData>(DELETE_DIALOG_ID);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (data?.onConfirm) {
      try {
        setIsLoading(true);
        await data.onConfirm();
        close();
      } catch (error: any) {
        console.error('Delete operation failed:', error);
        const errorMessage = error?.response?.data?.message || error?.message || t('common.error');
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const title = data?.title || t('common.confirm');
  const itemName = data?.itemName;
  const description = data?.description || (
    <>
      {t('common.deleteConfirmation')}{' '}
      {itemName && <span className="font-semibold text-foreground break-all">"{itemName}"</span>}?{' '}
      {t('common.actionCannotBeUndone')}
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={isLoading} className="cursor-pointer">
            {t('common.cancel')}
          </Button>
          <Button
            variant={data?.confirmVariant || 'destructive'}
            onClick={handleConfirm}
            disabled={isLoading}
            className="cursor-pointer"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {data?.confirmText || t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
